import Redactor from "../../../vendor/redactor/redactor";
import "../../../vendor/redactor/redactor.css";
import "../../css/_redactor-disabled.scss";

function basicSanitize(html) {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/on\w+="[^"]*"/gi, "")
    .replace(/on\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/<img[^>]+onerror[^>]*>/gi, "")
    .replace(/<br\s*\/?>\s*(?:<br\s*\/?>\s*)+/g, "<br>");
}

function unwrapNode(el) {
  const parent = el.parentNode;
  if (!parent) {
    return;
  }
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
}

/** Разрешённые атрибуты по тегу (остальные снимаем). Пустой Set — без атрибутов. */
const ATTRS_BY_TAG = {
  A: new Set(["href", "target", "rel", "title"]),
  IMG: new Set(["src", "alt", "title", "width", "height", "loading"]),
};

function stripUnsafeAttributes(el) {
  const tag = el.tagName.toUpperCase();
  const allowed = ATTRS_BY_TAG[tag] || new Set();
  [...el.attributes].forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on")) {
      el.removeAttribute(attr.name);
      return;
    }
    if (tag === "A" && name === "href" && /^\s*javascript:/i.test(attr.value || "")) {
      el.removeAttribute(attr.name);
      return;
    }
    if (tag === "IMG" && name === "src" && /^\s*javascript:/i.test(attr.value || "")) {
      el.removeAttribute(attr.name);
      return;
    }
    if (!allowed.has(name)) {
      el.removeAttribute(attr.name);
    }
  });
}

function sanitizeHtmlAllowlist(html, allowedTags, attrStripper = stripUnsafeAttributes) {
  const prepped = basicSanitize(html || "");
  if (!prepped.trim()) {
    return "";
  }

  try {
    const doc = new DOMParser().parseFromString(
      `<div id="__wysiwyg_san">${prepped}</div>`,
      "text/html",
    );
    const root = doc.getElementById("__wysiwyg_san");
    if (!root) {
      return prepped;
    }

    let guard = 0;
    while (guard++ < 2000) {
      const descend = Array.from(root.getElementsByTagName("*")).reverse();
      let unwrapped = false;
      for (const el of descend) {
        const tag = el.tagName.toUpperCase();
        if (tag === "SCRIPT" || tag === "STYLE") {
          el.remove();
          continue;
        }
        if (!allowedTags.has(tag)) {
          unwrapNode(el);
          unwrapped = true;
          break;
        }
      }
      if (!unwrapped) {
        break;
      }
    }

    root.querySelectorAll("*").forEach((el) => attrStripper(el));

    return root.innerHTML;
  } catch (_e) {
    return basicSanitize(html || "");
  }
}

const ALLOWED_TAGS_WORK_CONTENT = new Set([
  "P",
  "BR",
  "STRONG",
  "B",
  "EM",
  "I",
  "UL",
  "OL",
  "LI",
  "A",
]);

const ALLOWED_TAGS_FULL = new Set([
  ...ALLOWED_TAGS_WORK_CONTENT,
  "H2",
  "H3",
  "H4",
  "BLOCKQUOTE",
  "PRE",
  "IMG",
]);

const ALLOWED_TAGS_NOTIFICATION = new Set([
  ...ALLOWED_TAGS_FULL,
  "H1",
  "H5",
  "H6",
  "FIGURE",
  "FIGCAPTION",
  "DIV",
  "SPAN",
  "HR",
  "TABLE",
  "TBODY",
  "THEAD",
  "TFOOT",
  "TR",
  "TD",
  "TH",
  "IFRAME",
]);

const NOTIFICATION_ATTRS_BY_TAG = {
  A: new Set(["href", "target", "rel", "title"]),
  IMG: new Set(["src", "alt", "title", "width", "height", "loading", "style"]),
  FIGURE: new Set(["class"]),
  DIV: new Set(["class", "style"]),
  SPAN: new Set(["class", "style"]),
  TABLE: new Set(["class", "style", "width", "cellpadding", "cellspacing", "border"]),
  TD: new Set(["class", "style", "colspan", "rowspan", "align", "valign", "width", "height"]),
  TH: new Set(["class", "style", "colspan", "rowspan", "align", "valign", "width", "height"]),
  TR: new Set(["class", "style"]),
  IFRAME: new Set([
    "src",
    "width",
    "height",
    "frameborder",
    "allow",
    "allowfullscreen",
    "title",
  ]),
};

const NOTIFICATION_IFRAME_SRC =
  /^(?:https?:)?\/\/(?:www\.)?(?:youtube\.com|youtube-nocookie\.com|player\.vimeo\.com)\//i;

function sanitizeStyleAttr(value) {
  const style = String(value || "").trim();
  if (!style || /expression|javascript|url\s*\(|@import/i.test(style)) {
    return "";
  }

  const safeParts = style
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) =>
      /^(?:border|border-width|border-style|border-color|display|margin|padding|max-width|width|height)\s*:/i.test(
        part,
      ),
    );

  return safeParts.join("; ");
}

function stripUnsafeAttributesForNotification(el) {
  const tag = el.tagName.toUpperCase();
  const allowed = NOTIFICATION_ATTRS_BY_TAG[tag] || ATTRS_BY_TAG[tag] || new Set();

  [...el.attributes].forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on")) {
      el.removeAttribute(attr.name);
      return;
    }
    if (tag === "A" && name === "href" && /^\s*javascript:/i.test(attr.value || "")) {
      el.removeAttribute(attr.name);
      return;
    }
    if (tag === "IMG" && name === "src" && /^\s*javascript:/i.test(attr.value || "")) {
      el.removeAttribute(attr.name);
      return;
    }
    if (tag === "IFRAME" && name === "src" && !NOTIFICATION_IFRAME_SRC.test(attr.value || "")) {
      el.removeAttribute(attr.name);
      return;
    }
    if ((tag === "IMG" || tag === "DIV" || tag === "SPAN" || tag === "TD" || tag === "TH" || tag === "TR" || tag === "TABLE") && name === "style") {
      const cleanedStyle = sanitizeStyleAttr(attr.value);
      if (cleanedStyle) {
        el.setAttribute("style", cleanedStyle);
      } else {
        el.removeAttribute(attr.name);
      }
      return;
    }
    if (!allowed.has(name)) {
      el.removeAttribute(attr.name);
    }
  });
}

function normalizeNotificationHtml(html) {
  return String(html || "")
    .replace(/<figure>\s*(<a[\s\S]*?<\/a>)\s*<\/figure>/gi, "$1")
    .replace(/<figure>\s*(<img[\s\S]*?>)\s*<\/figure>/gi, "$1")
    .replace(/\sdata-image="[^"]*"/gi, "")
    .replace(/\sdata-redactor-type="[^"]*"/gi, "");
}

function getNotificationEditorHtml(el) {
  if (!(el instanceof HTMLTextAreaElement)) {
    return typeof el.value === "string" ? el.value : "";
  }

  if (el.classList.contains("redactor-source-open")) {
    return el.value;
  }

  const box = el.closest(".redactor-box");
  const editor = box?.querySelector(".redactor-in");

  if (editor instanceof HTMLElement && editor.innerHTML.trim() !== "") {
    return editor.innerHTML;
  }

  return el.value || "";
}

function syncNotificationEditorValue(el) {
  const html = normalizeNotificationHtml(getNotificationEditorHtml(el));
  const cleaned = basicSanitize(html);
  el.value = cleaned;
  return cleaned;
}

function applySanitizedCode(editorApi, sanitizeFn) {
  if (!editorApi?.source?.getCode || !editorApi.source.setCode) {
    return;
  }
  const html = editorApi.source.getCode();
  const cleaned = sanitizeFn(html);
  if (html !== cleaned) {
    editorApi.source.setCode(cleaned);
  }
}

/** Просмотр отправленной заявки: поле недоступно, но нужен отрисованный HTML, а не сырой текст с тегами. */
function isWysiwygViewOnly(el) {
  return el instanceof HTMLElement && el.matches(":disabled");
}

function initWorkContentWysiwyg() {
  const nodes = document.querySelectorAll(".js-work-content-wysiwyg");

  if (!nodes.length) return;

  const sanitize = (h) =>
    sanitizeHtmlAllowlist(h, ALLOWED_TAGS_WORK_CONTENT);

  nodes.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;

    const viewOnly = isWysiwygViewOnly(el);

    const before = typeof el.value === "string" ? el.value : "";
    const normalized = sanitize(before);
    if (before !== normalized) {
      el.value = normalized;
    }

    Redactor(el, {
      buttons: viewOnly ? [] : ["bold", "italic", "lists", "link"],
      toolbar: !viewOnly,
      source: !viewOnly,
      linkTarget: "_blank",
      pastePlainText: true,
      pasteBlockTags: ["p"],
      pasteInlineTags: [],
      pasteLinkTarget: false,
      callbacks: {
        changed() {
          if (!viewOnly) {
            applySanitizedCode(this, sanitize);
            if (typeof this.source?.getCode === 'function') {
              el.value = this.source.getCode();
            }
            el.dispatchEvent(new Event('input', {bubbles: true}));
          }
        },
        started() {
          applySanitizedCode(this, sanitize);
          if (viewOnly) {
            this.enableReadOnly();
          }
        },
      },
    });
  });
}

function initWysiwyg() {
  const selector = ".js-wysiwyg";

  const nodes = document.querySelectorAll(selector);
  if (!nodes.length) return;

  const sanitize = (h) => sanitizeHtmlAllowlist(h, ALLOWED_TAGS_FULL);

  nodes.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;

    const viewOnly = isWysiwygViewOnly(el);

    const before = typeof el.value === "string" ? el.value : "";
    const normalized = sanitize(before);
    if (before !== normalized) {
      el.value = normalized;
    }

    Redactor(el, {
      buttons: viewOnly
        ? []
        : ["html", "format", "bold", "italic", "lists", "link", "image"],
      toolbar: !viewOnly,
      source: !viewOnly,
      imageUpload: "/images/redactor-upload",
      imageUploadParam: "image",
      imageResizable: !viewOnly,
      imagePosition: {
        left: "img-left",
        right: "img-right",
        center: "img-center",
      },
      callbacks: {
        changed() {
          if (!viewOnly) {
            applySanitizedCode(this, sanitize);
          }
        },
        started() {
          applySanitizedCode(this, sanitize);
          if (viewOnly) {
            this.enableReadOnly();
          }
        },
      },
      pastePlainText: true,
      pasteBlockTags: ["p"],
      pasteInlineTags: [],
      pasteLinkTarget: false,
      linkTarget: "_blank",
      formatting: ["p", "blockquote", "pre"],
    });
  });
}

function initWysiwygFree() {
  const selector = ".js-wysiwyg-free";

  const nodes = document.querySelectorAll(selector);
  if (!nodes.length) return;

  const sanitize = (h) => sanitizeHtmlAllowlist(h, ALLOWED_TAGS_FULL);

  nodes.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;

    const viewOnly = isWysiwygViewOnly(el);

    const before = typeof el.value === "string" ? el.value : "";
    const normalized = sanitize(before);
    if (before !== normalized) {
      el.value = normalized;
    }

    Redactor(el, {
      buttons: viewOnly ? [] : ["format", "bold", "italic", "lists", "link"],
      toolbar: !viewOnly,
      source: !viewOnly,
      imageUpload: "/images/redactor-upload",
      imageUploadParam: "image",
      imageResizable: !viewOnly,
      imagePosition: {
        left: "img-left",
        right: "img-right",
        center: "img-center",
      },
      callbacks: {
        changed() {
          if (!viewOnly) {
            applySanitizedCode(this, sanitize);
          }
        },
        started() {
          applySanitizedCode(this, sanitize);
          if (viewOnly) {
            this.enableReadOnly();
          }
        },
      },
      pastePlainText: true,
      pasteBlockTags: ["p"],
      pasteInlineTags: [],
      pasteLinkTarget: false,
      linkTarget: "_blank",
      formatting: ["p", "blockquote", "pre"],
    });
  });
}

function initParticipantNotificationWysiwyg() {
  const nodes = document.querySelectorAll(".js-wysiwyg-notification");
  if (!nodes.length) return;

  nodes.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;

    const viewOnly = isWysiwygViewOnly(el);

    const before = typeof el.value === "string" ? el.value : el.textContent || "";
    if (before && /<script/i.test(before)) {
      el.value = basicSanitize(before);
    }

    Redactor(el, {
      buttons: viewOnly
        ? []
        : ["html", "format", "bold", "italic", "lists", "link", "image"],
      toolbar: !viewOnly,
      source: !viewOnly,
      imageUpload: "/images/redactor-upload",
      imageUploadParam: "image",
      imageResizable: !viewOnly,
      imageFigure: false,
      imageObserve: false,
      autoparse: false,
      autoparseStart: false,
      autoparsePaste: false,
      autoparseImages: false,
      imagePosition: {
        left: "img-left",
        right: "img-right",
        center: "img-center",
      },
      callbacks: {
        "source.open": function openNotificationSource(html) {
          return normalizeNotificationHtml(html);
        },
        "source.close": function closeNotificationSource(html) {
          return normalizeNotificationHtml(html);
        },
        "source.closed": function syncNotificationAfterSourceClose() {
          syncNotificationEditorValue(el);
        },
        started() {
          if (viewOnly) {
            this.enableReadOnly();
          }
        },
      },
      pastePlainText: false,
      pasteClean: false,
      pasteBlockTags: ["p", "figure", "div", "table"],
      pasteInlineTags: ["a", "strong", "b", "em", "i", "img", "span"],
      pasteLinkTarget: "_blank",
      linkTarget: "_blank",
      formatting: ["p", "blockquote", "pre"],
    });
  });

  const form = document.querySelector(".work-formats-settings");
  if (form) {
    form.addEventListener(
      "submit",
      () => {
        nodes.forEach((el) => {
          if (el instanceof HTMLElement && !isWysiwygViewOnly(el)) {
            syncNotificationEditorValue(el);
          }
        });
      },
      true,
    );
  }
}

export { initWorkContentWysiwyg, initWysiwyg, initWysiwygFree, initParticipantNotificationWysiwyg };
